import { memo, useMemo, useState } from "react"

import type { AgentGuidanceChange } from "@productlane/lib/src/agent-recommendations"
import { changedGuidanceLines } from "@productlane/lib/src/agent-recommendations"
import { cn } from "@productlane/lib/src/browser"
import { Textarea } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { useAutosaveText } from "@/lib/use-autosave-text"
import { useZero } from "@/lib/zero"

const GuidanceEditor = memo(
	({
		recommendationId,
		change,
	}: {
		recommendationId: string
		change: AgentGuidanceChange
	}) => {
		const zero = useZero()
		const [draft, setDraft] = useState(change.after)
		useAutosaveText({
			value: draft,
			persistedValue: change.after,
			enabled: true,
			onSave: (after) => {
				void zero.mutate(
					mutators.agentRecommendations.editGuidance({
						id: recommendationId,
						after,
					}),
				)
			},
		})
		return (
			<Textarea
				name="agent-guidance-draft"
				aria-label="Workspace guidance"
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				minRows={6}
				autoFocus
			/>
		)
	},
)

export const GuidanceChange = memo(
	({
		recommendationId,
		change,
		editing,
	}: {
		recommendationId: string
		change: AgentGuidanceChange
		editing: boolean
	}) => {
		const lines = useMemo(
			() => changedGuidanceLines(change.before, change.after),
			[change.before, change.after],
		)
		if (editing) {
			return (
				<GuidanceEditor recommendationId={recommendationId} change={change} />
			)
		}
		return (
			<div className="flex flex-col gap-1.5 text-sm leading-6">
				{lines.map((line, index) => (
					<div
						key={`${line.type}-${index}`}
						className={cn(
							"rounded-md px-2 py-1",
							line.type === "removed" &&
								"bg-red-50 text-red-800 line-through dark:bg-red-500/10 dark:text-red-300",
							line.type === "added" &&
								"bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300",
						)}
					>
						{line.text}
					</div>
				))}
			</div>
		)
	},
)
